import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import Loading from '../../../../components/Loading';
import CardMenu from '../CardMenu';
import ProjectMarketModal from '../ProjectMarketModal';
import SettingsCard from '../SettingsCard';
import SettingsField from '../SettingsField';
import TagList from '../TagList';
import {
    MISSING_MARKET_CODE,
    deleteBrandProfile,
    generateBrandProfile,
    getBrandProfile,
    updateBrandProfile,
} from '../query';

const TOOLTIP_ID = 'brand-profile-field-info';

const EMPTY_FORM = {
    description: '',
    industry: '',
    brand_attributes: [],
    products_services: [],
    personas: [],
};

function formFromProfile(profile) {
    return {
        description: profile.description ?? '',
        industry: profile.industry ?? '',
        brand_attributes: profile.brand_attributes ?? [],
        products_services: profile.products_services ?? [],
        personas: profile.personas ?? [],
    };
}

// PATCH reads every list it is given as the complete desired list, so the body always carries
// all five fields -- there is no such thing as sending only what changed.
function payloadFromForm(form) {
    return {
        description: form.description,
        industry: form.industry,
        brand_attributes: form.brand_attributes,
        products_services: form.products_services,
        personas: form.personas.map((persona) => persona.label),
    };
}

/**
 * The project's AI brand profile: generated on demand, then hand-editable.
 *
 * A project starts with no profile at all (the API answers 404 for GET), so the card opens on
 * an empty state whose only action is "Generate". Generation is a synchronous AI call on the
 * server and takes a while, which is why the button locks into an explicit waiting state
 * instead of a plain spinner.
 *
 * Everything the AI returns is editable afterwards. Description and industry are free-text
 * overwrites; brand attributes, products/services and personas are label lists that PATCH
 * treats as the *complete desired list* -- a label dropped from what we send is deleted server
 * side -- so the form always submits the full list, never a delta. Personas carry AI-filled
 * role/expertise/interests that are not settable, so a hand-added persona sends only its label
 * and comes back with the rest empty.
 *
 * Re-profiling (POST ?force=true) and deleting throw hand-edits away, so they sit behind the
 * header menu rather than next to Save, and both confirm first.
 *
 * Generating also needs the project to have a market. When it does not, the API rejects the
 * call with MISSING_MARKET_CODE instead of profiling anything; rather than leave the user to
 * work that out from an error message, the country/language modal opens on the spot and the
 * generation that was refused runs again by itself once the market is saved.
 */
class BrandProfile extends Component {
    constructor() {
        super();

        this.onGenerate = this.onGenerate.bind(this);
        this.onFocusDomainField = this.onFocusDomainField.bind(this);
        this.onRegenerate = this.onRegenerate.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onReset = this.onReset.bind(this);
        this.onChangeField = this.onChangeField.bind(this);
        this.onChangeList = this.onChangeList.bind(this);
        this.onChangePersonas = this.onChangePersonas.bind(this);
        this.onMarketSaved = this.onMarketSaved.bind(this);
        this.onMarketModalClose = this.onMarketModalClose.bind(this);

        this.state = {
            loading: true,
            generating: false,
            saving: false,
            deleting: false,
            profile: null,
            form: EMPTY_FORM,
            error: '',
            isMarketModalOpen: false,
            // Whether the generation the market modal interrupted was a re-profile, so the
            // retry keeps asking for the same thing.
            marketRetryForce: false,
        };
    }

    componentDidMount() {
        this.getProfile();
    }

    get isDirty() {
        const { profile, form } = this.state;
        if (!profile) return false;
        return JSON.stringify(formFromProfile(profile)) !== JSON.stringify(form);
    }

    get isBusy() {
        const { generating, saving, deleting } = this.state;
        return generating || saving || deleting;
    }

    async getProfile() {
        try {
            const res = await getBrandProfile(this.props.projectId);
            if (res.status === 404) {
                this.setState({ loading: false, profile: null, form: EMPTY_FORM });
                return;
            }

            const data = await res.json();
            if (res.status !== 200) {
                this.setState({
                    loading: false,
                    error: data?.detail ?? this.props.t('global.something_went_wrong'),
                });
                return;
            }

            this.setState({ loading: false, profile: data, form: formFromProfile(data) });
        } catch (err) {
            this.setState({
                loading: false,
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    async generate(force) {
        try {
            this.setState({ generating: true, error: '' });

            const res = await generateBrandProfile(this.props.projectId, force);
            const data = await res.json();

            if (res.status !== 201) {
                // The error stays on the card either way: it is the whole story for any other
                // failure, and the reason still worth reading if the market modal is dismissed
                // instead of filled in.
                this.setState({
                    generating: false,
                    error: data?.detail ?? this.props.t('global.something_went_wrong'),
                    isMarketModalOpen: data?.code === MISSING_MARKET_CODE,
                    marketRetryForce: force,
                });
                return;
            }

            this.setState({ generating: false, profile: data, form: formFromProfile(data) });
            this.props.onProfileChange?.(data);
        } catch (err) {
            this.setState({
                generating: false,
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    onGenerate() {
        this.generate(false);
    }

    onRegenerate() {
        // Re-profiling throws away every hand-edit, so it always asks first.
        if (
            !window.confirm(
                this.props.t('ai_tracking.projects.settings.brand_profile.regenerate_confirm'),
            )
        ) {
            return;
        }
        this.generate(true);
    }

    /**
     * Pick generation back up now that the project has a market.
     *
     * Goes straight to generate() rather than through onRegenerate(): a re-profile has already
     * been confirmed once, and asking again after the market detour would read as a second,
     * unrelated question. The saved project travels up because the page (and the project block
     * it renders) still holds the copy without a market.
     */
    onMarketSaved(project) {
        this.setState({ isMarketModalOpen: false });
        this.props.onProjectUpdated?.(project);
        this.generate(this.state.marketRetryForce);
    }

    onMarketModalClose() {
        this.setState({ isMarketModalOpen: false });
    }

    async onDelete() {
        if (
            !window.confirm(
                this.props.t('ai_tracking.projects.settings.brand_profile.delete_confirm'),
            )
        ) {
            return;
        }

        try {
            this.setState({ deleting: true, error: '' });

            const res = await deleteBrandProfile(this.props.projectId);
            if (res.status !== 204) {
                const data = await res.json().catch(() => null);
                this.setState({
                    deleting: false,
                    error: data?.detail ?? this.props.t('global.something_went_wrong'),
                });
                return;
            }

            this.setState({ deleting: false, profile: null, form: EMPTY_FORM });
            this.props.onProfileChange?.(null);
        } catch (err) {
            this.setState({
                deleting: false,
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    onChangeField(field, value) {
        this.setState((prevState) => ({
            form: { ...prevState.form, [field]: value },
        }));
    }

    // Chip lists write straight through -- adding one with Enter or removing one with the X
    // saves by itself -- so the Save button below is only ever about description and industry.
    onChangeList(field, items) {
        this.setState(
            (prevState) => ({ form: { ...prevState.form, [field]: items } }),
            () => this.saveList(field, items),
        );
    }

    // Personas are edited as labels, but the form keeps whole objects so the AI-filled
    // role/expertise/interests survive an unrelated edit. A label with no existing object is
    // new, and the API fills nothing in for it.
    onChangePersonas(labels) {
        this.setState(
            (prevState) => ({
                form: {
                    ...prevState.form,
                    personas: labels.map(
                        (label) =>
                            prevState.form.personas.find((item) => item.label === label) ?? {
                                label,
                                global_role: '',
                                expertise: '',
                                likely_interests: [],
                            },
                    ),
                },
            }),
            () => this.saveList('personas', this.state.form.personas),
        );
    }

    /**
     * Persist one chip list on its own.
     *
     * The body pairs the edited list with the *last saved* description and industry rather
     * than what is currently on screen, so removing a tag never quietly commits a description
     * someone is still half way through typing -- that stays dirty for the Save button.
     *
     * The chip is already on screen by the time this runs, so a failed call has to put the
     * list back the way the server still has it; leaving it would show a change that was
     * never stored.
     */
    async saveList(field, items) {
        const { profile } = this.state;
        if (!profile) return;

        const saved = formFromProfile(profile);

        try {
            this.setState({ saving: true, error: '' });

            const res = await updateBrandProfile(
                this.props.projectId,
                payloadFromForm({ ...saved, [field]: items }),
            );
            const data = await res.json();

            if (res.status !== 200) {
                this.rollbackList(field, saved[field], data?.detail);
                return;
            }

            // Only the lists come back into the form; description and industry keep whatever
            // has been typed since. A persona added by hand returns with its AI fields empty,
            // which is the shape renderPersona already expects.
            this.setState((prevState) => ({
                saving: false,
                profile: data,
                form: {
                    ...prevState.form,
                    brand_attributes: data.brand_attributes ?? [],
                    products_services: data.products_services ?? [],
                    personas: data.personas ?? [],
                },
            }));
            this.props.onSaved?.();
        } catch (err) {
            this.rollbackList(field, saved[field], err?.message);
        }
    }

    rollbackList(field, savedItems, detail) {
        this.setState((prevState) => ({
            saving: false,
            form: { ...prevState.form, [field]: savedItems },
            error: detail ?? this.props.t('global.something_went_wrong'),
        }));
    }

    onReset() {
        this.setState((prevState) => ({
            form: prevState.profile ? formFromProfile(prevState.profile) : EMPTY_FORM,
        }));
    }

    async onSave() {
        try {
            this.setState({ saving: true, error: '' });

            const res = await updateBrandProfile(
                this.props.projectId,
                payloadFromForm(this.state.form),
            );
            const data = await res.json();

            if (res.status !== 200) {
                this.setState({
                    saving: false,
                    error: data?.detail ?? this.props.t('global.something_went_wrong'),
                });
                return;
            }

            this.setState({ saving: false, profile: data, form: formFromProfile(data) });
            this.props.onSaved?.();
        } catch (err) {
            this.setState({
                saving: false,
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    /**
     * Send the user to the domain field, which is in the project block at the top of this page.
     *
     * Found by name rather than by ref because that block is the shared project form, rendered
     * here as a page; reaching into it from this card would tie the two together for the sake of
     * one scroll.
     */
    onFocusDomainField() {
        const field = document.querySelector('input[name="own_domain"]');
        if (!field) return;

        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.focus({ preventScroll: true });
    }

    renderEmptyState() {
        const { t, hasDomain } = this.props;

        return (
            <div className="settings-empty">
                <i className="icon-ai-tracking settings-empty-icon" />
                <div className="settings-empty-title">
                    {t('ai_tracking.projects.settings.brand_profile.empty_title')}
                </div>
                <div className="settings-empty-text">
                    {hasDomain
                        ? t('ai_tracking.projects.settings.brand_profile.empty_text')
                        : t('ai_tracking.projects.settings.brand_profile.needs_domain')}
                </div>
                {/* Without a domain there is nothing to profile, but a button that says so
                    and then refuses to be pressed leaves the user to work out where the domain
                    is set. The form that sets it is at the top of this same page, so the button
                    takes them there instead of standing disabled. */}
                <button
                    type="button"
                    className="button button-filled button-primary width-sm btn d-flex gap-5 align-items-center justify-content-center"
                    disabled={this.state.generating}
                    onClick={hasDomain ? this.onGenerate : this.onFocusDomainField}
                >
                    {this.state.generating && <Loading onlyPreloader />}
                    {this.state.generating
                        ? t('ai_tracking.projects.settings.brand_profile.generating')
                        : hasDomain
                        ? t('ai_tracking.projects.settings.brand_profile.generate')
                        : t('ai_tracking.projects.settings.brand_profile.add_domain')}
                </button>
                {this.state.generating && (
                    <div className="settings-empty-text mt-10">
                        {t('ai_tracking.projects.settings.brand_profile.generating_hint')}
                    </div>
                )}
            </div>
        );
    }

    renderPersona(label) {
        const persona = this.state.form.personas.find((item) => item.label === label);
        const meta = [persona?.global_role, persona?.expertise]
            .filter(Boolean)
            .map((value) => value.replace(/_/g, ' '))
            .join(' · ');
        const interests = (persona?.likely_interests ?? []).map((interest) => interest.trim());

        return (
            <span className="settings-persona">
                <span className="settings-persona-label">{label}</span>
                {!!meta && <span className="settings-persona-meta">{meta}</span>}
                {interests.length > 0 && (
                    <span className="settings-persona-interests">{interests.join(', ')}</span>
                )}
            </span>
        );
    }

    renderForm() {
        const { t } = this.props;
        const { form } = this.state;
        const prefix = 'ai_tracking.projects.settings.brand_profile';
        const disabled = this.isBusy;

        return (
            <div className="settings-fields">
                <SettingsField
                    title={t(`${prefix}.description`)}
                    hint={t(`${prefix}.description_hint`)}
                    tooltip={t(`${prefix}.description_tooltip`)}
                    tooltipId={TOOLTIP_ID}
                >
                    <textarea
                        className="form-control settings-textarea"
                        rows={4}
                        value={form.description}
                        disabled={disabled}
                        onChange={(e) => this.onChangeField('description', e.target.value)}
                    />
                </SettingsField>

                <SettingsField
                    title={t(`${prefix}.industry`)}
                    hint={t(`${prefix}.industry_hint`)}
                    tooltip={t(`${prefix}.industry_tooltip`)}
                    tooltipId={TOOLTIP_ID}
                >
                    <input
                        className="form-control"
                        type="text"
                        value={form.industry}
                        disabled={disabled}
                        onChange={(e) => this.onChangeField('industry', e.target.value)}
                    />
                </SettingsField>

                <SettingsField
                    title={t(`${prefix}.identity`)}
                    hint={t(`${prefix}.identity_hint`)}
                    tooltip={t(`${prefix}.identity_tooltip`)}
                    tooltipId={TOOLTIP_ID}
                >
                    <TagList
                        items={form.brand_attributes}
                        disabled={disabled}
                        placeholder={t(`${prefix}.identity_placeholder`)}
                        onChange={(items) => this.onChangeList('brand_attributes', items)}
                    />
                </SettingsField>

                <SettingsField
                    title={t(`${prefix}.products`)}
                    hint={t(`${prefix}.products_hint`)}
                    tooltip={t(`${prefix}.products_tooltip`)}
                    tooltipId={TOOLTIP_ID}
                >
                    <TagList
                        items={form.products_services}
                        disabled={disabled}
                        placeholder={t(`${prefix}.products_placeholder`)}
                        onChange={(items) => this.onChangeList('products_services', items)}
                    />
                </SettingsField>

                <SettingsField
                    title={t(`${prefix}.personas`)}
                    hint={t(`${prefix}.personas_hint`)}
                    tooltip={t(`${prefix}.personas_tooltip`)}
                    tooltipId={TOOLTIP_ID}
                >
                    <TagList
                        containerClassName="settings-tags-stacked"
                        items={form.personas.map((persona) => persona.label)}
                        disabled={disabled}
                        placeholder={t(`${prefix}.personas_placeholder`)}
                        renderItem={(label) => this.renderPersona(label)}
                        onChange={this.onChangePersonas}
                    />
                </SettingsField>
            </div>
        );
    }

    render() {
        const { t } = this.props;
        const { loading, profile, saving } = this.state;
        const prefix = 'ai_tracking.projects.settings.brand_profile';

        const actions = profile && (
            <CardMenu
                label={t(`${prefix}.menu`)}
                disabled={this.isBusy}
                items={[
                    {
                        key: 'regenerate',
                        icon: 'icon-refresh',
                        label: this.state.generating
                            ? t(`${prefix}.generating`)
                            : t(`${prefix}.regenerate`),
                        onClick: this.onRegenerate,
                    },
                    {
                        key: 'delete',
                        icon: 'icon-trash-2',
                        label: t(`${prefix}.delete`),
                        danger: true,
                        onClick: this.onDelete,
                    },
                ]}
            />
        );

        const footer = profile && (
            <>
                {this.isDirty && (
                    <button
                        type="button"
                        className="button button-outline button-primary width-sm btn"
                        disabled={saving}
                        onClick={this.onReset}
                    >
                        {t(`${prefix}.discard`)}
                    </button>
                )}
                <button
                    type="button"
                    className="button button-filled button-primary width-sm btn d-flex gap-5 align-items-center justify-content-center"
                    disabled={!this.isDirty || this.isBusy}
                    onClick={this.onSave}
                >
                    {saving && <Loading onlyPreloader />}
                    {t(`${prefix}.save`)}
                </button>
            </>
        );

        return (
            <>
                <SettingsCard
                    title={t(`${prefix}.header`)}
                    subtitle={t(`${prefix}.subtitle`)}
                    actions={actions}
                    footer={footer}
                    footerNote={profile ? t(`${prefix}.save_note`) : ''}
                >
                    {loading && <Loading padding={10} />}
                    {!loading && !!this.state.error && (
                        <div className="competitors-error-message my-10">
                            <img src="/static/img/alert-icon.svg" alt="icon" />
                            {this.state.error}
                        </div>
                    )}
                    {!loading && !profile && this.renderEmptyState()}
                    {!loading && !!profile && this.renderForm()}
                    <Tooltip id={TOOLTIP_ID} place="top" className="settings-field-tooltip" />
                </SettingsCard>
                {this.state.isMarketModalOpen && (
                    <ProjectMarketModal
                        isOpen
                        project={this.props.project}
                        onSaved={this.onMarketSaved}
                        onClose={this.onMarketModalClose}
                    />
                )}
            </>
        );
    }
}

export default withTranslation()(BrandProfile);
